import React, { useState, useEffect, useCallback } from 'react';
import { admin, tireAppointments } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import SmartPagination from '@/components/SmartPagination';
import { Eye, Loader2, Pencil, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/currency-context';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-900',
  confirmed: 'bg-blue-100 text-blue-900',
  in_progress: 'bg-violet-100 text-violet-900',
  completed: 'bg-green-100 text-green-900',
  cancelled: 'bg-red-100 text-red-900',
};

const STATUS_LABELS = {
  pending: 'Beklemede',
  confirmed: 'Onaylandı',
  in_progress: 'İşlemde',
  completed: 'Tamamlandı',
  cancelled: 'İptal',
};

const PAGE_SIZE = 20;

function TireReservationsPanel() {
  const { formatPrice } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editStatus, setEditStatus] = useState('pending');
  const [editNotes, setEditNotes] = useState('');
  const [editFinalPrice, setEditFinalPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (dateFilter.trim()) params.date = dateFilter.trim();
      const res = await admin.tireAppointmentsList(params);
      setRows(res.data.appointments || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Lastik randevuları yüklenemedi');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openEdit = (apt) => {
    setEditRow(apt);
    setEditStatus(apt.status || 'pending');
    setEditNotes(apt.service_notes || '');
    setEditFinalPrice(apt.final_price != null ? String(apt.final_price) : '');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editRow?.id) return;
    try {
      setSaving(true);
      const payload = {
        status: editStatus,
        service_notes: editNotes || undefined,
      };
      const fp = parseFloat(String(editFinalPrice).replace(',', '.'), 10);
      if (editFinalPrice !== '' && !Number.isNaN(fp)) {
        payload.final_price = fp;
      }
      await tireAppointments.update(editRow.id, payload);
      toast.success('Randevu güncellendi');
      setEditOpen(false);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-tire-reservations">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="space-y-1.5">
          <Label className="text-xs">Durum</Label>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="pending">Beklemede</SelectItem>
              <SelectItem value="confirmed">Onaylandı</SelectItem>
              <SelectItem value="in_progress">İşlemde</SelectItem>
              <SelectItem value="completed">Tamamlandı</SelectItem>
              <SelectItem value="cancelled">İptal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Randevu tarihi (GG-AA-YYYY)</Label>
          <Input
            type="date"
            className="w-full sm:w-[200px]"
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
          />
        </div>
        <Button type="button" variant="outline" className="gap-2" onClick={() => fetchList()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Yenile
        </Button>
      </div>

      <div className="rounded-md border bg-white">
        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">Kayıt yok</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih / saat</TableHead>
                <TableHead>Servis</TableHead>
                <TableHead>Müşteri</TableHead>
                <TableHead>Araç / ebat</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">Tahmini</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((apt) => (
                <TableRow key={apt.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {apt.appointment_date || '—'}
                    <br />
                    <span className="text-muted-foreground">{apt.appointment_time || ''}</span>
                  </TableCell>
                  <TableCell className="max-w-[180px] text-sm">
                    {apt.service_center?.name || '—'}
                    {apt.service_center?.city && (
                      <span className="text-muted-foreground"> · {apt.service_center.city}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {apt.customer_name}
                    <br />
                    <span className="text-xs text-muted-foreground">{apt.customer_phone}</span>
                    {apt.user_info?.email && (
                      <span className="block text-xs text-muted-foreground">{apt.user_info.email}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {apt.vehicle_plate && <span className="block">{apt.vehicle_plate}</span>}
                    {apt.tire_size && <span>{apt.tire_size}</span>}
                    {!apt.vehicle_plate && !apt.tire_size && '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_BADGE[apt.status] || 'bg-slate-100'} variant="secondary">
                      {STATUS_LABELS[apt.status] || apt.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {formatPrice(apt.estimated_price || 0, 'EUR')}
                  </TableCell>
                  <TableCell>
                    <Button type="button" size="sm" variant="outline" onClick={() => openEdit(apt)} className="gap-1">
                      <Pencil className="h-3.5 w-3.5" />
                      Düzenle
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {total > 0 && (
        <SmartPagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={total}
          pageSize={PAGE_SIZE}
        />
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lastik randevusu</DialogTitle>
          </DialogHeader>
          {editRow && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {editRow.appointment_date} {editRow.appointment_time} · {editRow.customer_name}
              </p>
              <div className="space-y-1.5">
                <Label>Durum</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Servis notu</Label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  placeholder="İç not (müşteriye özel sayfada yoksa göstermeyin diye ayrı)"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Kesin fiyat (opsiyonel)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editFinalPrice}
                  onChange={(e) => setEditFinalPrice(e.target.value)}
                  placeholder="Boş bırakılabilir"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              İptal
            </Button>
            <Button onClick={saveEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CAREER_PAGE_SIZE = 20;

const CAREER_STATUS_BADGE = {
  new: 'bg-sky-100 text-sky-900',
};

const CAREER_STATUS_LABELS = {
  new: 'Yeni',
};

function formatDateTimeTr(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function CareerFormsPanel() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: CAREER_PAGE_SIZE };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await admin.careerApplicationsList(params);
      setRows(res.data.applications || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Kariyer başvuruları yüklenemedi');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(total / CAREER_PAGE_SIZE));

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRow(null);
    try {
      const res = await admin.careerApplicationGet(id);
      setDetailRow(res.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Detay alınamadı');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-career-applications">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="space-y-1.5">
          <Label className="text-xs">Durum</Label>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="new">Yeni</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" className="gap-2" onClick={() => fetchList()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Yenile
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Satırdaki başvuruya tıklayarak tüm bilgileri görüntüleyebilirsiniz.
      </p>

      <div className="rounded-md border bg-white">
        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            Henüz kariyer başvurusu yok
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Ad soyad</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>İlgi alanı</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="w-16 text-center"><span className="sr-only">Detay</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openDetail(r.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDetail(r.id);
                    }
                  }}
                >
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTimeTr(r.created_at)}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{r.full_name || '—'}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">{r.email || '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.phone || '—'}</TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm">{r.area_of_interest || '—'}</TableCell>
                  <TableCell>
                    <Badge
                      className={CAREER_STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-800'}
                      variant="secondary"
                    >
                      {CAREER_STATUS_LABELS[r.status] || r.status || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Eye className="mx-auto h-4 w-4 text-muted-foreground" aria-hidden />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {total > 0 && (
        <SmartPagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={total}
          pageSize={CAREER_PAGE_SIZE}
        />
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Kariyer başvurusu</DialogTitle>
          </DialogHeader>
          {detailLoading && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {!detailLoading && detailRow && (
            <div className="space-y-3 text-sm">
              <div className="grid gap-1">
                <span className="text-xs font-medium uppercase text-muted-foreground">Ad soyad</span>
                <span>{detailRow.full_name}</span>
              </div>
              <div className="grid gap-1">
                <span className="text-xs font-medium uppercase text-muted-foreground">E-posta</span>
                <a className="text-primary underline underline-offset-2" href={`mailto:${detailRow.email}`}>
                  {detailRow.email}
                </a>
              </div>
              {detailRow.phone ? (
                <div className="grid gap-1">
                  <span className="text-xs font-medium uppercase text-muted-foreground">Telefon</span>
                  <a className="text-primary underline underline-offset-2" href={`tel:${detailRow.phone}`}>
                    {detailRow.phone}
                  </a>
                </div>
              ) : null}
              <div className="grid gap-1">
                <span className="text-xs font-medium uppercase text-muted-foreground">İlgi alanı</span>
                <span>{detailRow.area_of_interest || '—'}</span>
              </div>
              <div className="grid gap-1">
                <span className="text-xs font-medium uppercase text-muted-foreground">Mesaj</span>
                <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-muted-foreground">
                  {detailRow.message || '—'}
                </p>
              </div>
              <div className="flex flex-wrap gap-4 border-t pt-3 text-xs text-muted-foreground">
                <span>
                  Dil: <strong className="text-foreground">{detailRow.locale || '—'}</strong>
                </span>
                <span>
                  Durum:{' '}
                  <strong className="text-foreground">
                    {CAREER_STATUS_LABELS[detailRow.status] || detailRow.status || '—'}
                  </strong>
                </span>
                <span>
                  Gönderim: <strong className="text-foreground">{formatDateTimeTr(detailRow.created_at)}</strong>
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground font-mono">ID: {detailRow.id}</div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDetailOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ReservationsTab = () => {
  return (
    <div className="space-y-4" data-testid="admin-reservations-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Rezervasyon ve Formlar</h1>
        <p className="text-sm text-slate-600 mt-1">
          Lastik servis randevuları ile kariyer / iş başvuru formlarını bu ekrandan takip edebilirsiniz.
        </p>
      </div>

      <Tabs defaultValue="tire" className="w-full">
        <TabsList className="mb-2 inline-flex w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="tire" data-testid="reservations-tab-tire">
            Lastik
          </TabsTrigger>
          <TabsTrigger value="careers" data-testid="reservations-tab-careers">
            Kariyer formları
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tire" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Lastik değişim randevuları</CardTitle>
              <CardDescription>
                Sitede oluşturulan lastik servis randevuları. Durum, not ve kesin fiyatı güncelleyebilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TireReservationsPanel />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="careers" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Kariyer başvuruları</CardTitle>
              <CardDescription>
                Careers / Kariyer sayfasından gelen iş başvuruları listede görünür; bir satıra tıklayınca tüm detaylar
                açılır.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CareerFormsPanel />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReservationsTab;
